import { useState } from 'react';
import {
  categoriesAdmin,
  ApiError,
  type Category,
} from '../lib/api.ts';
import { IconPicker, glyphFor } from './IconPicker.tsx';

interface Props {
  readonly categories: readonly Category[];
  readonly onChanged: () => void;
}

const MESSAGES: Record<string, string> = {
  slug_in_use: 'That short name is already taken.',
  category_cap_reached:
    'Eight is the maximum. Past that the icon row wraps and the panel '
    + 'stops being readable at a glance.',
  cannot_archive_fallback:
    'This is the fallback category. Something has to catch '
    + 'recategorised messages.',
  has_open_requests:
    'There are still open tickets here. Resolve or move them first.',
};

const slugify = (name: string): string =>
  name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 30);

export const Categories = ({ categories, onChanged }: Props) => {
  const [name, setName] = useState('');
  const [icon, setIcon] = useState('ti-clipboard-list');
  const [color, setColor] = useState('#0F6E56');
  const [siteInitiated, setSiteInitiated] = useState(true);
  const [sla, setSla] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState('');
  const [error, setError] = useState('');

  const fail = (e: unknown) => {
    const code = e instanceof ApiError ? e.code : 'unknown';
    setError(MESSAGES[code] ?? 'That did not work. Try again.');
  };

  const create = async () => {
    setBusy(true);
    setError('');
    try {
      await categoriesAdmin.create({
        name: name.trim(),
        // Derived, never typed. The slug is a routing key nobody sees,
        // and asking an admin to invent one is a chance to get it wrong.
        slug: slugify(name),
        icon,
        color,
        allowsSiteInitiated: siteInitiated,
        slaHours: sla ? Number(sla) : null,
      });
      setName('');
      setSla('');
      onChanged();
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  };

  const archive = async (id: string, label: string) => {
    // Inline, not window.confirm: native dialogs ignore the brand,
    // block the window, and behave inconsistently in WebView2.
    if (confirming !== id) {
      setConfirming(id);
      return;
    }
    setConfirming('');
    setBusy(true);
    setError('');
    try {
      await categoriesAdmin.archive(id);
      onChanged();
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  };

  const toggleFiling = async (c: Category) => {
    setBusy(true);
    setError('');
    try {
      await categoriesAdmin.update(c.id, {
        allowsSiteInitiated: !c.allows_site_initiated,
      });
      onChanged();
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  };

  const full = categories.length >= 8;

  return (
    <div className="noticeWrap">
      {error && <p className="err">{error}</p>}

      <p className="mono covHead">
        Categories &mdash; {categories.length} of 8
      </p>
      <ul className="covList">
        {categories.map((c) => (
          <li key={c.id}>
            <span>
              <span className="catGlyph" style={{ color: c.color }}>
                {glyphFor(c.icon)}
              </span>
              {c.name}
              {c.requires_ack && (
                <span className="pinAck"> ack required</span>
              )}
            </span>
            <span className="covWhen">
              {c.allows_site_initiated ? 'sites can file' : 'broadcast only'}
              <button
                className="linkish"
                disabled={busy}
                onClick={() => void toggleFiling(c)}
              >
                switch
              </button>
              <button
                className="linkish danger"
                disabled={busy}
                onClick={() => void archive(c.id, c.name)}
              >
                {confirming === c.id ? 'Confirm archive' : 'Archive'}
              </button>
            </span>
          </li>
        ))}
      </ul>

      <section className="composer">
        <p className="mono">Add a category</p>
        {full ? (
          <p className="hint">
            Eight is the maximum. Archive one first.
          </p>
        ) : (
          <>
            <div className="noticeRow">
              <input
                value={name}
                placeholder="Name, e.g. Security"
                onChange={(e) => setName(e.target.value)}
              />
              <input
                type="color"
                className="colorInput"
                value={color}
                onChange={(e) => setColor(e.target.value)}
                aria-label="Colour"
              />
            </div>
            <IconPicker value={icon} onChange={setIcon} />
            <div className="noticeRow">
              <label className="check">
                <input
                  type="checkbox"
                  checked={siteInitiated}
                  onChange={(e) => setSiteInitiated(e.target.checked)}
                />
                Sites can file here
              </label>
              <label className="check">
                SLA hours
                <input
                  className="slaInput"
                  inputMode="numeric"
                  value={sla}
                  placeholder="none"
                  onChange={(e) => setSla(e.target.value.replace(/\D/g, ''))}
                />
              </label>
            </div>
            <button
              className="primary"
              disabled={busy || slugify(name).length < 2}
              onClick={create}
            >
              {busy ? 'Adding...' : 'Add category'}
            </button>
          </>
        )}
      </section>
    </div>
  );
};
